import React, { useMemo } from 'react';
import { Modal, Button, Table, Typography, Space, Card, Statistic, Row, Col, Empty } from 'antd';
import { DownloadOutlined, DollarOutlined, LineChartOutlined, AreaChartOutlined, FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useDraggableModal } from "../Balancete/utils/modalDraggable";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoMCO from '../../../../imagens/logotipo.png';

const { Title, Text } = Typography;

const ModalFaturamento = ({ visible, faturamentoData, onClose, onMinimize, title, zIndex, onFocus }) => {
    const draggableProps = useDraggableModal(zIndex, onFocus, {
        visible,
        top: 40,
        modalKey: 'faturamento',
        initialWidth: 1000,
        initialHeight: 800
    });

    // Certifica-se de que faturamentoData seja um array
    const rawData = Array.isArray(faturamentoData) ? faturamentoData : [];

    // Ordenar do mais recente para o mais antigo (decrescente)
    const data = useMemo(() => {
        return [...rawData].sort((a, b) => {
            const dateA = a.competencia || a.mes || a.mesAno;
            const dateB = b.competencia || b.mes || b.mesAno;
            if (!dateA || !dateB) return 0;

            const [monthA, yearA] = dateA.split('/');
            const [monthB, yearB] = dateB.split('/');

            const timeA = new Date(yearA, monthA - 1).getTime();
            const timeB = new Date(yearB, monthB - 1).getTime();

            return timeB - timeA; // Decrescente
        });
    }, [rawData]);

    // Cálculo de estatísticas premium
    const stats = useMemo(() => {
        if (!data || data.length === 0) return { total: 0, media: 0, max: 0 };

        // Assumindo que a API retorna campos como 'valor', 'receita', ou 'receitaBruta'
        const getValor = (item) => item.valor || item.receitaBruta || item.receita || 0;

        const total = data.reduce((acc, curr) => acc + getValor(curr), 0);
        const media = total / data.length;
        const max = Math.max(...data.map(getValor));

        return { total, media, max };
    }, [data]);

    // Formatação de Moeda
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
    };

    // Definição das colunas da Tabela
    const columns = [
        {
            title: 'Mês/Ano',
            dataIndex: 'competencia',
            key: 'competencia',
            render: (text, record) => (
                <Text strong style={{ color: '#1e293b' }}>
                    {text || record.mes || record.mesAno || '-'}
                </Text>
            ),
        },
        {
            title: 'Receita Bruta (Faturamento)',
            dataIndex: 'receitaBruta',
            key: 'receitaBruta',
            align: 'right',
            render: (text, record) => {
                const val = text || record.valor || record.receita || 0;
                return (
                    <Text style={{ fontWeight: 500, color: '#059669' }}>
                        {formatCurrency(val)}
                    </Text>
                );
            },
        },
        {
            title: 'Alíquota',
            dataIndex: 'aliquota',
            key: 'aliquota',
            align: 'right',
            render: (text) => text ? `${Number(text).toFixed(2)}%` : '-',
        },
        {
            title: 'Valor Devido (Simples)',
            dataIndex: 'valorDevido',
            key: 'valorDevido',
            align: 'right',
            render: (text, record) => {
                const val = text || record.valorSimples || record.imposto || 0;
                return (
                    <Text style={{ fontWeight: 500, color: '#dc2626' }}>
                        {val > 0 ? formatCurrency(val) : '-'}
                    </Text>
                );
            },
        }
    ];

    const handleExportExcel = () => {
        // Lógica futura para exportar a tabela como Excel
        console.log("Exportar faturamento para Excel");
    };

    const handleExportPDF = () => {
        if (!data || data.length === 0) return;

        const img = new Image();
        img.src = logoMCO;

        const generatePDF = () => {
            const doc = new jsPDF();

            // Tenta adicionar o logo (mantendo a proporção original)
            try {
                const imgWidth = img.width;
                const imgHeight = img.height;
                const ratio = imgWidth / imgHeight;
                const finalHeight = 22; // Altura fixa aumentada
                const finalWidth = finalHeight * ratio; // Largura proporcional
                const pageWidth = doc.internal.pageSize.getWidth();
                const centerX = (pageWidth - finalWidth) / 2;
                doc.addImage(img, 'PNG', centerX, 10, finalWidth, finalHeight);
            } catch (e) {
                console.warn("Erro ao carregar logo no PDF", e);
            }

            // Resumo Estatístico (Alinhado à direita)
            doc.setFontSize(10);
            doc.setTextColor(71, 85, 105); // Slate 600
            doc.setFont("helvetica", "normal");

            // Linha divisória sutil
            doc.setDrawColor(226, 232, 240);
            doc.line(14, 38, 196, 38);

            // Título Principal
            doc.setFontSize(16);
            doc.setTextColor(15, 23, 42); // Slate 900
            doc.setFont("helvetica", "bold");
            doc.text("Extrato de Faturamento", 14, 48);

            // Subtítulo / Data de Emissão
            doc.setFontSize(9);
            doc.setTextColor(100, 116, 139); // Slate 500
            doc.setFont("helvetica", "normal");
            const dataEmissao = new Date().toLocaleDateString('pt-BR', { hour: '2-digit', minute: '2-digit' });
            doc.text(`Simples Nacional - Emitido em: ${dataEmissao}`, 14, 54);

            // Tabela de Lançamentos
            const tableColumn = ["Competência", "Receita Bruta", "Alíquota", "Valor DAS"];
            const tableRows = [];

            let totalReceita = 0;
            let totalDevido = 0;

            data.forEach(item => {
                const valReceita = item.valor || item.receitaBruta || item.receita || 0;
                const valDevido = item.valorDevido || item.imposto || item.valorSimples || 0;
                const aliquota = item.aliquota ? `${Number(item.aliquota).toFixed(2)}%` : '-';

                totalReceita += valReceita;
                totalDevido += valDevido;

                tableRows.push([
                    item.competencia || item.mes || item.mesAno || '-',
                    formatCurrency(valReceita),
                    aliquota,
                    valDevido > 0 ? formatCurrency(valDevido) : '-'
                ]);
            });

            autoTable(doc, {
                head: [tableColumn],
                body: tableRows,
                foot: [['Total', formatCurrency(totalReceita), '-', formatCurrency(totalDevido)]],
                startY: 62,
                theme: 'striped',
                styles: {
                    fontSize: 10,
                    cellPadding: 6,
                    textColor: [51, 65, 85],
                },
                headStyles: {
                    fillColor: [3, 97, 167], // Azul MCO
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    halign: 'center'
                },
                footStyles: {
                    fillColor: [241, 245, 249],
                    textColor: [15, 23, 42],
                    fontStyle: 'bold',
                },
                columnStyles: {
                    0: { halign: 'center', cellWidth: 35 },
                    1: { halign: 'center' },
                    2: { halign: 'center', cellWidth: 30 },
                    3: { halign: 'center' }
                },
                alternateRowStyles: {
                    fillColor: [248, 250, 252]
                }
            });

            doc.save(`Extrato_Faturamento_${new Date().getTime()}.pdf`);
        };

        // Pré-carrega a imagem
        img.onload = () => generatePDF();
        img.onerror = () => generatePDF();
    };

    return (
        <Modal
            title={title || "Consulta Faturamento (Últimos 12 Meses)"}
            open={visible}
            onCancel={onClose}
            width={1000}
            {...draggableProps}
            styles={{
                content: {
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '16px',
                    overflow: 'hidden'
                },
                body: {
                    flex: 1,
                    padding: '24px',
                    overflowY: "auto",
                    background: '#f8fafc',
                }
            }}
            footer={[
                <Button key="pdf" icon={<FilePdfOutlined />} onClick={handleExportPDF} style={{ borderColor: '#ef4444', color: '#ef4444' }}>
                    Exportar PDF
                </Button>,
                <Button key="excel" icon={<FileExcelOutlined />} onClick={handleExportExcel} style={{ borderColor: '#10b981', color: '#10b981' }}>
                    Exportar Excel
                </Button>,
                <Button key="close" onClick={onClose} type="primary" style={{ background: '#3b82f6' }}>
                    Fechar
                </Button>
            ]}
        >
            {data.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

                    {/* Cards de Resumo */}
                    <Row gutter={16}>
                        <Col span={8}>
                            <Card bordered={false} style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                                <Statistic
                                    title={<span style={{ color: '#64748b', fontWeight: 600 }}>Faturamento Total (12m)</span>}
                                    value={stats.total}
                                    precision={2}
                                    prefix={<DollarOutlined style={{ color: '#3b82f6' }} />}
                                    formatter={(val) => formatCurrency(val)}
                                    valueStyle={{ color: '#1e293b', fontWeight: 700, fontSize: '24px' }}
                                />
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card bordered={false} style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                                <Statistic
                                    title={<span style={{ color: '#64748b', fontWeight: 600 }}>Média Mensal</span>}
                                    value={stats.media}
                                    precision={2}
                                    prefix={<LineChartOutlined style={{ color: '#10b981' }} />}
                                    formatter={(val) => formatCurrency(val)}
                                    valueStyle={{ color: '#1e293b', fontWeight: 700, fontSize: '24px' }}
                                />
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card bordered={false} style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                                <Statistic
                                    title={<span style={{ color: '#64748b', fontWeight: 600 }}>Maior Faturamento</span>}
                                    value={stats.max}
                                    precision={2}
                                    prefix={<AreaChartOutlined style={{ color: '#f59e0b' }} />}
                                    formatter={(val) => formatCurrency(val)}
                                    valueStyle={{ color: '#1e293b', fontWeight: 700, fontSize: '24px' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Tabela de Dados */}
                    <Card bordered={false} style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <Table
                            dataSource={data}
                            columns={columns}
                            rowKey={(record, index) => record.competencia || index}
                            pagination={false}
                            size="middle"
                            bordered={false}
                            summary={(pageData) => {
                                let totalReceita = 0;
                                let totalDevido = 0;
                                pageData.forEach(({ receitaBruta, valor, receita, valorDevido, imposto, valorSimples }) => {
                                    totalReceita += (receitaBruta || valor || receita || 0);
                                    totalDevido += (valorDevido || imposto || valorSimples || 0);
                                });
                                return (
                                    <Table.Summary.Row style={{ background: '#f1f5f9', fontWeight: 700 }}>
                                        <Table.Summary.Cell index={0}>Total 12 Meses</Table.Summary.Cell>
                                        <Table.Summary.Cell index={1} align="right">
                                            <Text style={{ color: '#059669' }}>{formatCurrency(totalReceita)}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={2} />
                                        <Table.Summary.Cell index={3} align="right">
                                            <Text style={{ color: '#dc2626' }}>{formatCurrency(totalDevido)}</Text>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                );
                            }}
                        />
                    </Card>
                </div>
            ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            <Text style={{ color: '#64748b', fontSize: '16px' }}>
                                Nenhum dado de faturamento disponível para os últimos 12 meses.
                            </Text>
                        }
                    />
                </div>
            )}
        </Modal>
    );
};

export default ModalFaturamento;
